/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api } from "../../../lib/auth";
import { Trophy, TrendingUp, Users, Target, BarChart3, Activity, Loader2 } from "lucide-react";

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

export function AnalyticsView() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/t/re/analytics/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="p-12 flex items-center justify-center text-black/45"><Loader2 className="animate-spin" /></div>;
  const k = d.kpis;
  const maxRev = Math.max(1, ...d.revenue_trend.map((r) => r.revenue));

  return (
    <div data-testid="re-analytics-view" className="space-y-6">
      {/* KPI band */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI title="Active marketers" value={k.marketers_active} icon={Users} accent="bg-emerald-50 text-emerald-700" />
        <KPI title="Frozen" value={k.marketers_frozen} icon={Users} accent="bg-red-50 text-red-700" />
        <KPI title="Total leads" value={k.leads_total} icon={Activity} accent="bg-blue-50 text-blue-700" />
        <KPI title="Won deals" value={k.leads_won} icon={Trophy} accent="bg-amber-50 text-amber-700" />
        <KPI title="Conversion rate" value={`${k.conv_rate}%`} icon={Target} accent="bg-violet-50 text-violet-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Leaderboard */}
        <div className="lg:col-span-2 bg-white border border-black/8 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4"><Trophy size={16} className="text-amber-600" /><div className="font-display font-extrabold text-lg">Marketer leaderboard</div></div>
          {d.leaderboard.length === 0 ? (
            <div className="text-sm text-black/45 text-center py-6">No marketer activity yet</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wide font-bold text-black/55">
                <tr><th className="text-left pb-2">#</th><th className="text-left pb-2">Name</th><th className="text-right pb-2">Sales</th><th className="text-right pb-2">Revenue</th><th className="text-right pb-2">Conv</th></tr>
              </thead>
              <tbody>
                {d.leaderboard.slice(0, 10).map((m, i) => (
                  <tr key={m.marketer_id} className="border-t border-black/5" data-testid={`re-leader-${i}`}>
                    <td className="py-2.5 font-display font-extrabold text-black/40">{i + 1}</td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center text-xs font-bold">{(m.name || "?")[0]}</div>
                        <span className="font-bold">{m.name}</span>
                        {m.status === "frozen" && <span className="text-[9px] uppercase font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Frozen</span>}
                      </div>
                    </td>
                    <td className="py-2.5 text-right">{m.sales_count}</td>
                    <td className="py-2.5 text-right font-bold text-emerald-700">{ngn(m.revenue)}</td>
                    <td className="py-2.5 text-right"><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${m.conversion_rate >= 50 ? "bg-emerald-100 text-emerald-700" : m.conversion_rate >= 20 ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-700"}`}>{m.conversion_rate}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Lead funnel */}
        <div className="bg-white border border-black/8 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4"><BarChart3 size={16} className="text-blue-600" /><div className="font-display font-extrabold text-lg">Lead funnel</div></div>
          {Object.keys(d.lead_funnel).length === 0 ? (
            <div className="text-sm text-black/45 text-center py-6">No leads yet</div>
          ) : (
            <div className="space-y-2.5">
              {["new", "contacted", "interested", "inspection", "negotiation", "closed_won", "closed_lost"].map((stage) => {
                const v = d.lead_funnel[stage] || 0;
                const max = Math.max(1, ...Object.values(d.lead_funnel));
                const pct = (v / max) * 100;
                const color = stage === "closed_won" ? "bg-emerald-500" : stage === "closed_lost" ? "bg-red-400" : "bg-blue-500";
                return (
                  <div key={stage}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="capitalize font-bold text-black/70">{stage.replace("_", " ")}</span>
                      <span className="tabular-nums">{v}</span>
                    </div>
                    <div className="h-2 bg-black/5 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Revenue trend */}
      <div className="bg-white border border-black/8 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4"><TrendingUp size={16} className="text-emerald-600" /><div className="font-display font-extrabold text-lg">Revenue — last 30 days</div></div>
        {d.revenue_trend.length === 0 ? (
          <div className="text-sm text-black/45 text-center py-6">No revenue activity in last 30 days</div>
        ) : (
          <div className="flex items-end gap-1 h-40">
            {d.revenue_trend.map((r) => (
              <div key={r.date} className="flex-1 group relative">
                <div className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t" style={{ height: `${(r.revenue / maxRev) * 100}%` }} />
                <div className="opacity-0 group-hover:opacity-100 absolute -top-9 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                  {r.date}: {ngn(r.revenue)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Source breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white border border-black/8 rounded-2xl p-5">
          <div className="font-display font-extrabold text-lg mb-3">Lead sources</div>
          {Object.entries(d.lead_sources).map(([k, v]) => {
            const total = Object.values(d.lead_sources).reduce((a, b) => a + b, 0);
            const pct = total ? Math.round((v / total) * 100) : 0;
            return (
              <div key={k} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0">
                <span className="text-sm capitalize text-black/70">{k.replace("-", " ")}</span>
                <div className="flex items-center gap-3">
                  <div className="w-32 h-1.5 bg-black/5 rounded-full"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                  <span className="text-sm font-bold tabular-nums w-12 text-right">{v} ({pct}%)</span>
                </div>
              </div>
            );
          })}
          {Object.keys(d.lead_sources).length === 0 && <div className="text-sm text-black/45 text-center py-4">No data</div>}
        </div>
      </div>
    </div>
  );
}

function KPI({ title, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-black/8 rounded-2xl p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase font-bold text-black/55">{title}</div>
          <div className="mt-1 font-display font-extrabold text-2xl text-black">{value}</div>
        </div>
        {Icon && <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent || "bg-black/5 text-black/55"}`}><Icon size={18} /></div>}
      </div>
    </div>
  );
}
